import React from "react"
import { FormattedMessage, useIntl } from "react-intl"
import { useStyles } from "./Styles"
import { useHistory } from "react-router-dom"
import { useFormik } from "formik"
import { PromocodeFormValidation } from "../../../../../utils/validationSchema"
import { DiscountTypes } from "../../../../../utils/enum"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from "@material-ui/core"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import DeleteForeverIcon from "@material-ui/icons/DeleteForever"
import { NamedRoutes } from "../../../../../routes"
import { FormLoader } from "../../../../Loader/FormLoader"
import { getPriceableType } from "../../../../../utils/helpers"
import UserAutocomplete from "./UserAutocomplete"
import UserGroupAutocomplete from "./UserGroupAutocomplete"
import ExternalEntitySelector from "./ExternalEntitySelector"
import ExternalEntityGroupAutocomplete from "./ExternalEntityGroupAutocomplete"
import PayablesManager from "./PayablesManager"

// Reducer actions
const RULE_ACTIONS = {
  SET_USER: 'SET_USER',
  REMOVE_USER: 'REMOVE_USER',
  SET_EXTERNAL_ENTITY_TYPE: 'SET_EXTERNAL_ENTITY_TYPE',
  SET_EXTERNAL_ENTITY_REFERENCE: 'SET_EXTERNAL_ENTITY_REFERENCE',
  ADD_USER_GROUP: 'ADD_USER_GROUP',
  REMOVE_USER_GROUP: 'REMOVE_USER_GROUP',
  ADD_EXTERNAL_ENTITY_GROUP: 'ADD_EXTERNAL_ENTITY_GROUP',
  REMOVE_EXTERNAL_ENTITY_GROUP: 'REMOVE_EXTERNAL_ENTITY_GROUP',
  SET_PAYABLES: 'SET_PAYABLES',
  ADD_RULE: 'ADD_RULE',
  REMOVE_RULE: 'REMOVE_RULE',
  INIT_RULES: 'INIT_RULES'
}

// Helper to create an empty rule
const createEmptyRule = () => ({
  user_id: null,
  user: {},
  external_entity_reference: null,
  external_entity_type: null,
  user_groups: [],
  user_group_ids: [],
  external_entity_groups: [],
  external_entity_group_ids: [],
  payables: []
})

// Reducer for managing rules state
const rulesReducer = (state, action) => {
  const { ruleIndex, payload } = action

  switch (action.type) {
    case RULE_ACTIONS.INIT_RULES:
      const initializedRules = (payload.rules || []).map(rule => ({
        ...rule,
        user_group_ids: Array.isArray(rule.user_groups) && rule.user_groups.length > 0
          ? rule.user_groups.map(group => group.id)
          : [],
        external_entity_group_ids: Array.isArray(rule.external_entity_groups) && rule.external_entity_groups.length > 0
          ? rule.external_entity_groups.map(group => group.id)
          : []
      }))
      return {
        rules: initializedRules
      }

    case RULE_ACTIONS.SET_USER:
      return {
        ...state,
        rules: state.rules.map((rule, index) =>
          index === ruleIndex
            ? { ...rule, user_id: payload.user.id, user: payload.user }
            : rule
        )
      }

    case RULE_ACTIONS.REMOVE_USER:
      return {
        ...state,
        rules: state.rules.map((rule, index) =>
          index === ruleIndex
            ? { ...rule, user_id: null, user: {} }
            : rule
        )
      }

    case RULE_ACTIONS.SET_EXTERNAL_ENTITY_TYPE:
      return {
        ...state,
        rules: state.rules.map((rule, index) =>
          index === ruleIndex
            ? {
              ...rule,
              external_entity_type: payload.entityType,
              external_entity_reference: ''
            }
            : rule
        )
      }

    case RULE_ACTIONS.SET_EXTERNAL_ENTITY_REFERENCE:
      return {
        ...state,
        rules: state.rules.map((rule, index) =>
          index === ruleIndex
            ? { ...rule, external_entity_reference: payload.entityReference }
            : rule
        )
      }

    case RULE_ACTIONS.ADD_USER_GROUP:
      return {
        ...state,
        rules: state.rules.map((rule, index) =>
          index === ruleIndex
            ? {
              ...rule,
              user_groups: [...(rule.user_groups || []), payload.group],
              user_group_ids: [...(rule.user_group_ids || []), payload.group.id]
            }
            : rule
        )
      }

    case RULE_ACTIONS.REMOVE_USER_GROUP:
      return {
        ...state,
        rules: state.rules.map((rule, index) =>
          index === ruleIndex
            ? {
              ...rule,
              user_groups: (rule.user_groups || []).filter(g => g.id !== payload.groupId),
              user_group_ids: (rule.user_group_ids || []).filter(id => id !== payload.groupId)
            }
            : rule
        )
      }

    case RULE_ACTIONS.ADD_EXTERNAL_ENTITY_GROUP:
      return {
        ...state,
        rules: state.rules.map((rule, index) =>
          index === ruleIndex
            ? {
              ...rule,
              external_entity_groups: [...(rule.external_entity_groups || []), payload.group],
              external_entity_group_ids: [...(rule.external_entity_group_ids || []), payload.group.id]
            }
            : rule
        )
      }

    case RULE_ACTIONS.REMOVE_EXTERNAL_ENTITY_GROUP:
      return {
        ...state,
        rules: state.rules.map((rule, index) =>
          index === ruleIndex
            ? {
              ...rule,
              external_entity_groups: (rule.external_entity_groups || []).filter(g => g.id !== payload.groupId),
              external_entity_group_ids: (rule.external_entity_group_ids || []).filter(id => id !== payload.groupId)
            }
            : rule
        )
      }

    case RULE_ACTIONS.SET_PAYABLES:
      return {
        ...state,
        rules: state.rules.map((rule, index) =>
          index === ruleIndex
            ? { ...rule, payables: payload.payables }
            : rule
        )
      }

    case RULE_ACTIONS.ADD_RULE:
      return {
        ...state,
        rules: [...state.rules, createEmptyRule()]
      }

    case RULE_ACTIONS.REMOVE_RULE:
      // Allow removing all rules
      return {
        ...state,
        rules: state.rules.filter((_, index) => index !== ruleIndex)
      }

    default:
      return state
  }
}

const PromocodeForm = ({ externalServiceHandlers, promocodeData, handleNotificationCall, onSubmit }) => {
  const intl = useIntl()
  const classes = useStyles()
  const history = useHistory()

  // Initialize rules state with reducer
  const [rulesState, dispatch] = React.useReducer(rulesReducer, {
    rules: []
  })

  // Track expanded accordion panels
  const [expandedPanels, setExpandedPanels] = React.useState([0])

  const PromocodeFormik = useFormik(
    {
      initialValues: {
        title: promocodeData?.title || '',
        code: promocodeData?.code || '',
        start_date: promocodeData?.start_date || '',
        end_date: promocodeData?.end_date || '',
        usage_limit: promocodeData?.usage_limit !== undefined ? promocodeData.usage_limit : null,
        discount_type: promocodeData?.discount_type || DiscountTypes[0].value,
        discount_value: promocodeData?.discount_value || 0,
        max_discount: promocodeData?.max_discount !== undefined ? promocodeData.max_discount : null,
        is_active: promocodeData?.is_active || false,
        rules: rulesState.rules
      },
      validateOnMount: true,
      validateOnBlur: false,
      validateOnChange: true,
      validationSchema: PromocodeFormValidation,
      onSubmit: ((values, actions) => {
        // Clean up rules to only send necessary fields (remove user and user_groups objects)
        const cleanedRules = values.rules.map(rule => ({
          user_id: rule.user_id,
          external_entity_reference: rule.external_entity_reference === '' ? null : rule.external_entity_reference,
          external_entity_type: rule.external_entity_type === '' ? null : rule.external_entity_type,
          user_group_ids: rule.user_group_ids,
          external_entity_group_ids: rule.external_entity_group_ids,
          payables: (rule.payables || []).map(payable => ({
            payable_type: payable.payable_type,
            payable_id: payable.payable_id,
            priceable_type: !!payable.priceable_id ? getPriceableType(payable.payable_type) : null,
            priceable_id: payable.priceable_id || null
          }))
        }))

        const PromocodeFormPayload = {
          title: values.title,
          code: values.code,
          start_date: values.start_date || null,
          end_date: values.end_date || null,
          usage_limit: values.usage_limit || null,
          discount_type: values.discount_type,
          discount_value: values.discount_value,
          max_discount: values.max_discount || null,
          is_active: values.is_active,
          rules: cleanedRules
        }
        onSubmit(PromocodeFormPayload, actions)
      })
    }
  )

  // Sync rules state with Formik whenever rules change
  React.useEffect(() => {
    PromocodeFormik.setFieldValue('rules', rulesState.rules)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesState.rules])

  const handleIsActiveChange = (event) => {
    PromocodeFormik.setFieldValue('is_active', !JSON.parse(event.target.value))
  }

  // Initialize from promocodeData (if editing)
  React.useEffect(() => {
    if (promocodeData?.rules && promocodeData.rules.length > 0) {
      dispatch({
        type: RULE_ACTIONS.INIT_RULES,
        payload: { rules: promocodeData.rules }
      })
      // Expand all panels initially
      setExpandedPanels(promocodeData.rules.map((_, index) => index))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promocodeData])

  // Set max_discount to null when discount_type is flat
  React.useEffect(() => {
    if (PromocodeFormik.values.discount_type === 'flat') {
      PromocodeFormik.setFieldValue('max_discount', null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PromocodeFormik.values.discount_type])

  // Handle accordion expand/collapse
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanels(prev =>
      isExpanded
        ? [...prev, panel]
        : prev.filter(p => p !== panel)
    )
  }

  // Handle add rule
  const handleAddRule = () => {
    dispatch({ type: RULE_ACTIONS.ADD_RULE })
    // Auto-expand the new rule
    setExpandedPanels(prev => [...prev, rulesState.rules.length])
  }

  // Handle remove rule
  const handleRemoveRule = (ruleIndex) => {
    dispatch({ type: RULE_ACTIONS.REMOVE_RULE, ruleIndex })
    // Remove from expanded panels
    setExpandedPanels(prev => prev.filter(p => p !== ruleIndex).map(p => p > ruleIndex ? p - 1 : p))
  }

  // Handle user selection
  const handleUserSelect = (ruleIndex) => (user) => {
    dispatch({ type: RULE_ACTIONS.SET_USER, ruleIndex, payload: { user } })
  }

  // Handle user removal
  const handleUserRemove = (ruleIndex) => () => {
    dispatch({ type: RULE_ACTIONS.REMOVE_USER, ruleIndex })
  }

  // Handle external entity type change
  const handleEntityTypeChange = (ruleIndex) => (entityType) => {
    dispatch({ type: RULE_ACTIONS.SET_EXTERNAL_ENTITY_TYPE, ruleIndex, payload: { entityType } })
  }

  // Handle external entity reference change
  const handleEntityReferenceChange = (ruleIndex) => (entityReference) => {
    dispatch({ type: RULE_ACTIONS.SET_EXTERNAL_ENTITY_REFERENCE, ruleIndex, payload: { entityReference } })
  }

  // Handle external entity reference blur
  const handleEntityReferenceBlur = (ruleIndex) => () => {
    PromocodeFormik.setFieldTouched(`rules[${ruleIndex}].external_entity_reference`, true)
  }

  // Handle user group add
  const handleUserGroupAdd = (ruleIndex) => (group) => {
    dispatch({ type: RULE_ACTIONS.ADD_USER_GROUP, ruleIndex, payload: { group } })
  }

  // Handle user group remove
  const handleUserGroupRemove = (ruleIndex) => (groupId) => {
    dispatch({ type: RULE_ACTIONS.REMOVE_USER_GROUP, ruleIndex, payload: { groupId } })
  }

  // Handle external entity group add
  const handleExternalEntityGroupAdd = (ruleIndex) => (group) => {
    dispatch({ type: RULE_ACTIONS.ADD_EXTERNAL_ENTITY_GROUP, ruleIndex, payload: { group } })
  }

  // Handle external entity group remove
  const handleExternalEntityGroupRemove = (ruleIndex) => (groupId) => {
    dispatch({ type: RULE_ACTIONS.REMOVE_EXTERNAL_ENTITY_GROUP, ruleIndex, payload: { groupId } })
  }

  // Handle payables change
  const handlePayablesChange = (ruleIndex) => (payables) => {
    dispatch({ type: RULE_ACTIONS.SET_PAYABLES, ruleIndex, payload: { payables } })
  }

  return (
    <form onSubmit={PromocodeFormik.handleSubmit} noValidate autoComplete="off"
      className={classes.formWrapper}>

      {/* Title Field */}
      <div className={classes.BasicInfoRow}>
        <InputLabel id="Promocode.Title">
          <FormattedMessage id='Fields.label.PromocodeTitle' />
        </InputLabel>
        <FormControl variant="outlined">
          <TextField
            placeholder={intl.formatMessage({ id: 'Promocode.placeholder.Title' })}
            id="Promocode.Title"
            type={'text'}
            margin="dense"
            variant="outlined"
            name='title'
            value={PromocodeFormik.values.title}
            onChange={PromocodeFormik.handleChange}
            onBlur={PromocodeFormik.handleBlur}
            helperText={PromocodeFormik.errors.title && PromocodeFormik.touched.title && PromocodeFormik.errors.title}
            error={PromocodeFormik.errors.title && PromocodeFormik.touched.title !== undefined}
          />
        </FormControl>
      </div>

      {/* Code Field */}
      <div className={classes.BasicInfoRow}>
        <InputLabel id="Promocode.Code">
          <FormattedMessage id='Fields.label.PromocodeCode' />
        </InputLabel>
        <FormControl variant="outlined">
          <TextField
            placeholder={intl.formatMessage({ id: 'Promocode.placeholder.Code' })}
            id="Promocode.Code"
            type={'text'}
            margin="dense"
            variant="outlined"
            name='code'
            value={PromocodeFormik.values.code}
            onChange={PromocodeFormik.handleChange}
            onBlur={PromocodeFormik.handleBlur}
            helperText={PromocodeFormik.errors.code && PromocodeFormik.touched.code && PromocodeFormik.errors.code}
            error={PromocodeFormik.errors.code && PromocodeFormik.touched.code !== undefined}
          />
        </FormControl>
      </div>

      {/* Start Date Field */}
      <div className={classes.BasicInfoRow}>
        <InputLabel id="Promocode.StartDate">
          <FormattedMessage id='Fields.label.StartDate' />
        </InputLabel>
        <FormControl variant="outlined">
          <TextField
            id="Promocode.StartDate"
            type="date"
            margin="dense"
            variant="outlined"
            name='start_date'
            value={PromocodeFormik.values.start_date}
            onChange={PromocodeFormik.handleChange}
            onBlur={PromocodeFormik.handleBlur}
            InputLabelProps={{
              shrink: true
            }}
            helperText={PromocodeFormik.errors.start_date && PromocodeFormik.touched.start_date && PromocodeFormik.errors.start_date}
            error={PromocodeFormik.errors.start_date && PromocodeFormik.touched.start_date !== undefined}
          />
        </FormControl>
      </div>

      {/* End Date Field */}
      <div className={classes.BasicInfoRow}>
        <InputLabel id="Promocode.EndDate">
          <FormattedMessage id='Fields.label.EndDate' />
        </InputLabel>
        <FormControl variant="outlined">
          <TextField
            id="Promocode.EndDate"
            type="date"
            margin="dense"
            variant="outlined"
            name='end_date'
            value={PromocodeFormik.values.end_date}
            onChange={PromocodeFormik.handleChange}
            onBlur={PromocodeFormik.handleBlur}
            InputLabelProps={{
              shrink: true
            }}
            helperText={PromocodeFormik.errors.end_date && PromocodeFormik.touched.end_date && PromocodeFormik.errors.end_date}
            error={PromocodeFormik.errors.end_date && PromocodeFormik.touched.end_date !== undefined}
          />
        </FormControl>
      </div>

      {/* Discount Type Field */}
      <div className={classes.BasicInfoRow}>
        <InputLabel id="Promocode.DiscountType">
          <FormattedMessage id='Fields.label.DiscountType' />
        </InputLabel>
        <FormControl variant="outlined" margin="dense">
          <Select
            labelId="Promocode.DiscountType"
            id="Promocode.DiscountType"
            name="discount_type"
            value={PromocodeFormik.values.discount_type}
            onChange={PromocodeFormik.handleChange}
            onBlur={PromocodeFormik.handleBlur}
            displayEmpty
            error={PromocodeFormik.errors.discount_type && PromocodeFormik.touched.discount_type !== undefined}
            margin="dense"
          >
            {DiscountTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <FormattedMessage id={type.label} />
              </MenuItem>
            ))}
          </Select>
          {PromocodeFormik.errors.discount_type && PromocodeFormik.touched.discount_type && (
            <FormHelperText error={true}>{PromocodeFormik.errors.discount_type}</FormHelperText>
          )}
        </FormControl>
      </div>

      {/* Discount Value Field */}
      <div className={classes.BasicInfoRow}>
        <InputLabel id="Promocode.DiscountValue">
          <FormattedMessage id='Fields.label.DiscountValue' />
        </InputLabel>
        <FormControl variant="outlined">
          <TextField
            placeholder={intl.formatMessage({ id: 'Promocode.placeholder.DiscountValue' })}
            id="Promocode.DiscountValue"
            type={'number'}
            margin="dense"
            variant="outlined"
            name='discount_value'
            value={PromocodeFormik.values.discount_value}
            onChange={PromocodeFormik.handleChange}
            onBlur={PromocodeFormik.handleBlur}
            InputProps={{
              inputProps: {
                min: 0,
                ...(PromocodeFormik.values.discount_type === 'percentage' && { max: 100 }),
              },
              endAdornment: <InputAdornment position="end">{PromocodeFormik.values.discount_type === 'percentage' ? '%' : <FormattedMessage id='egp' />}</InputAdornment>
            }}
            helperText={PromocodeFormik.errors.discount_value && PromocodeFormik.touched.discount_value && PromocodeFormik.errors.discount_value}
            error={PromocodeFormik.errors.discount_value && PromocodeFormik.touched.discount_value !== undefined}
          />
        </FormControl>
      </div>

      {/* Max Discount Field */}
      <div className={classes.BasicInfoRow}>
        <InputLabel id="Promocode.MaxDiscount">
          <FormattedMessage id='Fields.label.MaxDiscount' />
        </InputLabel>
        <FormControl variant="outlined">
          <TextField
            placeholder={intl.formatMessage({ id: 'Promocode.placeholder.MaxDiscount' })}
            id="Promocode.MaxDiscount"
            type={'number'}
            margin="dense"
            variant="outlined"
            name='max_discount'
            value={PromocodeFormik.values.max_discount ?? ''}
            onChange={PromocodeFormik.handleChange}
            onBlur={PromocodeFormik.handleBlur}
            disabled={PromocodeFormik.values.discount_type === 'flat'}
            InputProps={{
              inputProps: {
                min: 0
              },
              endAdornment: <InputAdornment position="end"><FormattedMessage id='egp' /></InputAdornment>
            }}
            helperText={PromocodeFormik.errors.max_discount && PromocodeFormik.touched.max_discount && PromocodeFormik.errors.max_discount}
            error={PromocodeFormik.errors.max_discount && PromocodeFormik.touched.max_discount !== undefined}
          />
        </FormControl>
      </div>

      {/* Usage Limit Field */}
      <div className={classes.BasicInfoRow}>
        <InputLabel id="Promocode.UsageLimit">
          <FormattedMessage id='Fields.label.UsageLimit' />
        </InputLabel>
        <FormControl variant="outlined">
          <TextField
            placeholder={intl.formatMessage({ id: 'Promocode.placeholder.UsageLimit' })}
            id="Promocode.UsageLimit"
            type={'number'}
            margin="dense"
            variant="outlined"
            name='usage_limit'
            value={PromocodeFormik.values.usage_limit ?? ''}
            onChange={PromocodeFormik.handleChange}
            onBlur={PromocodeFormik.handleBlur}
            InputProps={{
              inputProps: {
                min: 0
              }
            }}
            helperText={PromocodeFormik.errors.usage_limit && PromocodeFormik.touched.usage_limit && PromocodeFormik.errors.usage_limit}
            error={PromocodeFormik.errors.usage_limit && PromocodeFormik.touched.usage_limit !== undefined}
          />
        </FormControl>
      </div>

      {/* Is Active Switch */}
      <div className={classes.BasicInfoRow}>
        <InputLabel id="Promocode.IsActive">
          <FormattedMessage id='Fields.label.IsActive' />:
        </InputLabel>
        <FormControl variant="outlined">
          <FormControlLabel
            label={PromocodeFormik.values.is_active ? intl.formatMessage({ id: 'Promocode.List.Table.Active.Option.Active' }) : intl.formatMessage({ id: 'Promocode.List.Table.Active.Option.inActive' })}
            value={PromocodeFormik.values.is_active}
            control={
              <Switch
                color="primary"
                checked={PromocodeFormik.values.is_active}
                value={PromocodeFormik.values.is_active}
              />
            }
            onChange={handleIsActiveChange}
          />
        </FormControl>
      </div>

      <div>
        <Typography variant="h3" className={classes.PromocodeRulesTitle}>
          <FormattedMessage id="Promocode.Rules.Title" />
        </Typography>

        {rulesState.rules.length === 0 && (
          <Typography variant="body2" style={{ marginBottom: '16px', color: '#666' }}>
            <FormattedMessage id='Promocode.Rules.noRules' />
          </Typography>
        )}

        {rulesState.rules.map((rule, ruleIndex) => (
          <Accordion
            key={ruleIndex}
            expanded={expandedPanels.includes(ruleIndex)}
            onChange={handleAccordionChange(ruleIndex)}
            className={classes.ruleAccordion}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`rule-${ruleIndex}-content`}
              id={`rule-${ruleIndex}-header`}
              className={classes.accordionSummary}
            >
              <div style={{ width: '100%', display: 'flex', gap: '8px', alignItems: 'baseline' }}>
                <Typography variant="h4" className={classes.PromocodeSingleRuleTitle}>
                  <FormattedMessage id='Promocode.Rule.Title' values={{ index: ruleIndex + 1 }} />
                </Typography>
                {PromocodeFormik.errors?.rules?.[ruleIndex] &&
                  (typeof PromocodeFormik.errors.rules[ruleIndex] === 'string' ||
                    React.isValidElement(PromocodeFormik.errors.rules[ruleIndex])) && (
                    <FormHelperText error={true}>
                      {PromocodeFormik.errors.rules[ruleIndex]}
                    </FormHelperText>
                  )}
              </div>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <div style={{ width: '100%' }}>
                {/* User Selection */}
                <div className={classes.BasicInfoRow}>
                  <InputLabel id={`Promocode.Rule${ruleIndex}.User`}>
                    <FormattedMessage id='Fields.input.PromocodeRuleUser.label' />
                  </InputLabel>
                  <UserAutocomplete
                    selectedUser={!!Object.keys(rule?.user || {}).length ? rule.user : {}}
                    onUserSelect={handleUserSelect(ruleIndex)}
                    onUserRemove={handleUserRemove(ruleIndex)}
                    handleNotificationCall={handleNotificationCall}
                  />
                </div>

                <Divider style={{ margin: '12px 0' }} />

                {/* User Group Selection */}
                <div className={classes.BasicInfoRow}>
                  <InputLabel id={`Promocode.Rule${ruleIndex}.UserGroups`}>
                    <FormattedMessage id='Fields.input.PromocodeRuleUserGroups.label' />
                  </InputLabel>
                  <UserGroupAutocomplete
                    selectedUserGroups={rule.user_groups || []}
                    onUserGroupAdd={handleUserGroupAdd(ruleIndex)}
                    onUserGroupRemove={handleUserGroupRemove(ruleIndex)}
                    handleNotificationCall={handleNotificationCall}
                  />
                </div>

                <Divider style={{ margin: '12px 0' }} />

                {/* External Entity Selection */}
                <ExternalEntitySelector
                  selectedEntityType={rule.external_entity_type}
                  selectedEntityReference={rule.external_entity_reference}
                  onEntityTypeChange={handleEntityTypeChange(ruleIndex)}
                  onEntityReferenceChange={handleEntityReferenceChange(ruleIndex)}
                  onBlurReference={handleEntityReferenceBlur(ruleIndex)}
                  promocodeFormik={PromocodeFormik}
                  formikErrors={PromocodeFormik.errors?.rules?.[ruleIndex]?.external_entity_reference}
                  formikTouched={PromocodeFormik.touched?.rules?.[ruleIndex]?.external_entity_reference}
                />

                <Divider style={{ margin: '12px 0' }} />

                {/* External Entity Group Selection */}
                <div className={classes.BasicInfoRow}>
                  <InputLabel id={`Promocode.Rule${ruleIndex}.ExternalEntityGroups`}>
                    <FormattedMessage id='Fields.input.PromocodeRuleExternalEntityGroups.label' />
                  </InputLabel>
                  <ExternalEntityGroupAutocomplete
                    selectedExternalEntityGroups={rule.external_entity_groups || []}
                    onExternalEntityGroupAdd={handleExternalEntityGroupAdd(ruleIndex)}
                    onExternalEntityGroupRemove={handleExternalEntityGroupRemove(ruleIndex)}
                    handleNotificationCall={handleNotificationCall}
                    externalServiceHandlers={externalServiceHandlers}
                  />
                </div>

                <Divider style={{ margin: '12px 0' }} />

                {/* Payables Manager */}
                <PayablesManager
                  payables={rule.payables || []}
                  onPayablesChange={handlePayablesChange(ruleIndex)}
                  handleNotificationCall={handleNotificationCall}
                  ruleIndex={ruleIndex}
                  formikErrors={PromocodeFormik.errors}
                  formikTouched={PromocodeFormik.touched}
                  setFieldTouched={PromocodeFormik.setFieldTouched}
                />

                {/* Remove Rule Button */}
                <div className={classes.removeWrapper}>
                  <Button
                    variant="contained"
                    color="secondary"
                    className={classes.removeRecord}
                    startIcon={<DeleteForeverIcon />}
                    onClick={() => handleRemoveRule(ruleIndex)}
                  >
                    <FormattedMessage id='Promocode.Rule.remove' />
                  </Button>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Add New Rule Button */}
        <Button
          className={classes.AddNewRecord}
          variant="contained"
          color="primary"
          onClick={handleAddRule}
        >
          <FormattedMessage id='Promocode.Rule.addRecord' />
        </Button>
      </div>
      {/* Submit Row */}
      <div className={classes.SubmitRow}>
        <button
          type="submit"
          disabled={PromocodeFormik.isSubmitting || !PromocodeFormik.isValid}
          className={classes.AddAdminSubmit}
        >
          <FormattedMessage id='AdditionalInfo.button.submit' />
        </button>
        <Button onClick={() => history.push(NamedRoutes.subscription.promocodes.list)} color="primary">
          <FormattedMessage id='ChangePhone.DialogActions.cancel' />
        </Button>
      </div>
      {PromocodeFormik.isSubmitting && <FormLoader loading={true} />}
    </form>
  )
}
export default PromocodeForm
